import _ from 'lodash';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

/**
 * In-memory store for runtime-only trend item values: state that is derived at runtime,
 * item-type independent, and must never be persisted. Keeping such values out of the item stores
 * means recomputing them can never dirty persisted state, so item stores can rely on the default
 * auto-push-on-change persistence without creating worksteps on every recompute.*
 */

export interface ItemYAxisExtremes {
  /** Raw minimum across the item's axis alignment group (or the user-set minimum) */
  yAxisMin: number | string;
  /** Raw maximum across the item's axis alignment group (or the user-set maximum) */
  yAxisMax: number | string;
  /** Buffered extremes actually applied to the chart axis */
  yAxisConfig: { min: number; max: number };
}

export interface ItemRuntimeState {
  /**
   * Computed y-axis extremes keyed by item id. Only parent items get entries; children render on
   * their parent's axis and fall back through `isChildOf` at read time.
   */
  yAxisExtremes: Record<string, ItemYAxisExtremes>;
}

export interface ItemRuntimeActions {
  /** Replaces the whole map — used by the lane recompute, which covers every alignable item */
  setAllYAxisExtremes(yAxisExtremes: Record<string, ItemYAxisExtremes>): void;
  /** Merges entries for a subset of items — used by manual zoom and data-arrival updates */
  mergeYAxisExtremes(yAxisExtremes: Record<string, ItemYAxisExtremes>): void;
  reset(): void;
}

export const initialState: ItemRuntimeState = Object.freeze({ yAxisExtremes: {} });

const STORE_NAME = 'ItemRuntime';

const itemRuntimeActionsFactory: ActionsFactory<ItemRuntimeState, ItemRuntimeActions> = (set, get, resetState) => ({
  setAllYAxisExtremes: (yAxisExtremes) => {
    if (_.isEqual(get().yAxisExtremes, yAxisExtremes)) {
      return;
    }
    set((draft) => {
      draft.yAxisExtremes = yAxisExtremes;
    });
  },

  mergeYAxisExtremes: (yAxisExtremes) => {
    const current = get().yAxisExtremes;
    if (Object.entries(yAxisExtremes).every(([id, value]) => _.isEqual(current[id], value))) {
      return;
    }
    set((draft) => {
      Object.assign(draft.yAxisExtremes, yAxisExtremes);
    });
  },

  reset: resetState,
});

const [ItemRuntimeStoreProvider, useItemRuntimeStoreInstance, useItemRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  itemRuntimeActionsFactory,
);

export { ItemRuntimeStoreProvider };

export const useYAxisExtremes = () => useItemRuntimeSelector((state) => state.yAxisExtremes);

export const useItemRuntimeActions = () => useItemRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateItemRuntimeStore(initialStateOverrides?: Partial<ItemRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    itemRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
